import { useCallback, useEffect, useState } from 'react';
import ticketTierService from '../services/ticketTierService';
import { useDialog } from '../context';
import { AffixInput, Btn, Field, Help, Inner, RemoveX, Row, RowText, SubPanel, TextInput, fmtWhen, usd } from './host/kit';

/**
 * Host-only ticket-tier manager — the Eventbrite-style "Early Bird / GA / Regular"
 * editor. Each tier is a named, self-priced option the buyer SEES and SELECTS at
 * checkout (replacing the "hunt for a promo code" friction). Optional per-tier
 * cutoffs: a sell-by date and/or a quantity cap. All tiers draw from the event's
 * overall capacity; a cap just limits how many of THAT option can sell.
 *
 * Placed right next to ticket pricing on the create/edit form and on the event-ops
 * dashboard. Renders inside EventCreatePage's outer <form>, so this uses plain divs
 * and type="button" throughout (nested <form> is invalid HTML).
 *
 * Presentation: the TICKET OPTIONS sub-panel of the host event handoff
 * (docs/plans/2026-09-host-event-edit/handoff/README.md, step 3), built from ./host/kit
 * and expected to render inside a `.hev` scope.
 *
 * Props:
 *   - eventId    — required to load/create tiers. Null (fresh draft) → disabled empty state.
 *   - onChange   — optional callback(tiers) after any mutation, so the parent can react
 *                  (e.g. flip its pricing preview to "tiered").
 *   - className  — optional wrapper override.
 */
export default function TicketTiersSection({ eventId, onChange, className = '' }) {
  const { showAlert, showConfirm } = useDialog();

  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(!!eventId);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Add/edit-tier form state. editingId is null when adding a new option, or the id
  // of the option being edited.
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [capacity, setCapacity] = useState('');
  const [salesEndAt, setSalesEndAt] = useState('');

  const emit = useCallback((next) => { if (onChange) onChange(next); }, [onChange]);

  const refresh = useCallback(async () => {
    if (!eventId) { setTiers([]); setLoading(false); return; }
    setLoading(true);
    const r = await ticketTierService.listForEvent(eventId);
    if (r.success) { setTiers(r.data || []); emit(r.data || []); }
    setLoading(false);
  }, [eventId, emit]);

  useEffect(() => { refresh(); }, [refresh]);

  const resetForm = () => { setEditingId(null); setName(''); setPrice(''); setCapacity(''); setSalesEndAt(''); };

  // ISO → the "yyyy-MM-ddThh:mm" value a datetime-local input expects (in local time).
  const toLocalInput = (iso) => {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // Open the form pre-filled with an existing option to edit it.
  const startEdit = (t) => {
    setEditingId(t.id);
    setName(t.name);
    setPrice((t.priceCents / 100).toFixed(2));
    setCapacity(t.capacity != null ? String(t.capacity) : '');
    setSalesEndAt(t.salesEndAt ? toLocalInput(t.salesEndAt) : '');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!eventId || !name.trim() || price === '' || submitting) return;
    setSubmitting(true);
    const body = {
      name: name.trim(),
      priceCents: Math.round(parseFloat(price) * 100),
      capacity: capacity ? parseInt(capacity, 10) : null,
      salesEndAt: salesEndAt ? new Date(salesEndAt).toISOString() : null,
    };

    const r = editingId
      ? await ticketTierService.update(editingId, body)
      : await ticketTierService.create(eventId, { ...body, sortOrder: tiers.length });

    if (r.success && r.data) {
      const next = editingId
        ? tiers.map((x) => (x.id === editingId ? r.data : x))
        : [...tiers, r.data];
      setTiers(next);
      emit(next);
      resetForm();
      setShowForm(false);
    } else {
      await showAlert({ title: 'Could not save option', message: r.error || 'Please try again.', type: 'error' });
    }
    setSubmitting(false);
  };

  const handleDelete = async (t) => {
    const ok = await showConfirm({
      title: 'DELETE TICKET OPTION',
      message: `Delete “${t.name}”? Tickets already sold at this price are kept; it just stops being offered.`,
      confirmLabel: 'DELETE',
    });
    if (!ok) return;
    const r = await ticketTierService.remove(t.id);
    if (r.success) {
      const next = tiers.filter((x) => x.id !== t.id);
      setTiers(next);
      emit(next);
    }
  };

  const toggleActive = async (t) => {
    const r = await ticketTierService.update(t.id, { isActive: !t.isActive });
    if (r.success && r.data) {
      const next = tiers.map((x) => (x.id === t.id ? r.data : x));
      setTiers(next);
      emit(next);
    }
  };

  // Reorder by swapping sortOrder with the neighbor, persisting both.
  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= tiers.length) return;
    const a = tiers[index];
    const b = tiers[target];
    // Optimistic local swap.
    const next = [...tiers];
    next[index] = b; next[target] = a;
    setTiers(next);
    emit(next);
    await Promise.all([
      ticketTierService.update(a.id, { sortOrder: target }),
      ticketTierService.update(b.id, { sortOrder: index }),
    ]);
  };

  // `n AVAILABLE · UNTIL SAT OCT 3, 7:00 PM · 4 SOLD · HIDDEN` — the row's meta line.
  const metaLine = (t) => {
    const bits = [t.capacity != null ? `${t.capacity} AVAILABLE` : 'NO LIMIT'];
    if (t.salesEndAt) bits.push(`UNTIL ${fmtWhen(t.salesEndAt)}`);
    if (t.capacity != null) bits.push(`${t.soldCount}/${t.capacity} SOLD`);
    else if (t.soldCount > 0) bits.push(`${t.soldCount} SOLD`);
    if (!t.isActive) bits.push('HIDDEN');
    else if (t.unavailableReason === 'ended') bits.push('ENDED');
    else if (t.unavailableReason === 'sold_out') bits.push('SOLD OUT');
    return bits.join(' · ');
  };

  const toggleForm = () => {
    if (showForm) { setShowForm(false); resetForm(); } else { resetForm(); setShowForm(true); }
  };

  const arrowStyle = { border: 'none', background: 'none', fontSize: 9, lineHeight: 1, color: '#6b7280', cursor: 'pointer', padding: 0 };
  const textBtnStyle = { border: 'none', background: 'none', padding: '0 4px', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: '#6b7280', cursor: 'pointer', whiteSpace: 'nowrap' };

  return (
    <SubPanel
      title="TICKET OPTIONS"
      sub="Early Bird, GA, Regular — buyers pick one at checkout."
      className={className}
      right={(
        <Btn size="sm" onClick={toggleForm} disabled={!eventId} style={{ flexShrink: 0 }}>
          {showForm ? 'CLOSE' : '+ ADD OPTION'}
        </Btn>
      )}
    >
      {!eventId && (
        <Help>Save the event basics first (title + date) — ticket options unlock once your draft is created.</Help>
      )}

      {eventId && loading && <Help>Loading…</Help>}

      {eventId && !loading && tiers.map((t, i) => (
        <Row key={t.id}>
          <span style={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" style={{ ...arrowStyle, opacity: i === 0 ? 0.3 : 1 }}>▲</button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === tiers.length - 1} aria-label="Move down" style={{ ...arrowStyle, opacity: i === tiers.length - 1 ? 0.3 : 1 }}>▼</button>
          </span>
          <RowText title={<span style={{ color: t.isActive ? '#000' : '#9ca3af', textDecoration: t.isActive ? 'none' : 'line-through' }}>{t.name}</span>} meta={metaLine(t)} bold />
          <span style={{ fontSize: 14, fontWeight: 800, whiteSpace: 'nowrap' }}>{usd(t.priceCents / 100)}</span>
          <button type="button" onClick={() => startEdit(t)} style={textBtnStyle}>EDIT</button>
          <button type="button" onClick={() => toggleActive(t)} style={textBtnStyle}>{t.isActive ? 'HIDE' : 'SHOW'}</button>
          <RemoveX onClick={() => handleDelete(t)} label="Delete option" />
        </Row>
      ))}

      {eventId && showForm && (
        <Inner grid>
          <Field label="NAME" small>
            <TextInput
              size="sm" type="text" value={name} onChange={(e) => setName(e.target.value)}
              maxLength={80} placeholder="Early Bird"
            />
          </Field>
          <Field label="PRICE ($)" small>
            <AffixInput
              prefix="$" size="sm" type="number" value={price} onChange={(e) => setPrice(e.target.value)}
              min={0} step={0.01} placeholder="20.00"
            />
          </Field>
          <Field label="QUANTITY (OPTIONAL)" small>
            <TextInput
              size="sm" type="number" value={capacity} onChange={(e) => setCapacity(e.target.value)}
              min={1} placeholder="No limit"
            />
          </Field>
          <Field label="SELL UNTIL (OPTIONAL)" small>
            <TextInput size="sm" type="datetime-local" value={salesEndAt} onChange={(e) => setSalesEndAt(e.target.value)} />
          </Field>
          <Btn className="hev-span2" full onClick={handleSave} disabled={submitting}>
            {submitting ? 'SAVING…' : (editingId ? 'SAVE CHANGES' : 'ADD OPTION')}
          </Btn>
        </Inner>
      )}

      {eventId && !loading && tiers.length === 0 && !showForm && (
        <span className="hev-caps-muted">ONE PRICE FOR EVERYONE — NO OPTIONS YET.</span>
      )}
    </SubPanel>
  );
}
